import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatEuro, formatPercent, formatQuantity, formatCryptoQuantity, formatNumber } from '../../utils/currency';
import { formatShortDate, formatDate } from '../../utils/date';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Paper,
  
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Legend, AreaChart, Area, BarChart, Bar, Line } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
// Removed unused PieChartIcon import per lint
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getStocks, StockDto } from '../../services/stocksApi';
import { computeHoldings, aggregatePortfolio, groupBySector, allocationByType, holdingsToCsv, HoldingAnalysis, PortfolioAggregate } from './analysis/portfolioAnalytics';
import { buildPerformance, PortfolioPerformanceSummary } from './analysis/performanceAnalytics';
import DownloadIcon from '@mui/icons-material/Download';
import PageHeader from '../../components/layout/PageHeader';
import TabPanel from '../../components/layout/TabPanel';
import AutoGrid from '../../components/layout/AutoGrid';

// Legacy inline interfaces replaced by portfolioAnalytics util types

interface PerformanceMetric {
  name: string;
  value: number;
  benchmark?: number;
  format: 'currency' | 'percentage';
}

const COLORS = ['#1976d2', '#ff9800', '#388e3c', '#d32f2f', '#7b1fa2', '#00796b', '#f57c00', '#5d4037'];

const PortfolioAnalysisPage: React.FC = () => {
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [timeframe, setTimeframe] = useState<'6M'|'1Y'|'2Y'|'5Y'|'ALL'>('1Y');

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allStocks = await getStocks();
      setStocks(allStocks);
      
    } catch (err) {
      console.error('Error loading portfolio data:', err);
      setError('Não foi possível carregar os dados da carteira');
    } finally {
      setLoading(false);
    }
  };

  const portfolioHoldings: HoldingAnalysis[] = useMemo(() => computeHoldings(stocks), [stocks]);
  const aggregate: PortfolioAggregate = useMemo(() => aggregatePortfolio(portfolioHoldings), [portfolioHoldings]);

  const sectorAllocation = useMemo(() => groupBySector(portfolioHoldings), [portfolioHoldings]);

  const performanceSummary: PortfolioPerformanceSummary | null = useMemo(()=> {
    if(!stocks.length) return null;
    return buildPerformance(stocks, timeframe);
  }, [stocks, timeframe]);

  const performanceMetrics = useMemo((): PerformanceMetric[] => {
    const annualized = performanceSummary?.annualizedReturnPct ?? aggregate.totalReturnPct;
    const vol = performanceSummary?.volatilityAnnualPct ?? 0;
    const maxDD = performanceSummary?.maxDrawdownPct ?? 0;
    return [
      { name: 'Valor total da carteira', value: aggregate.totalValue, format: 'currency' },
      { name: 'Total investido', value: aggregate.totalInvested, format: 'currency' },
      { name: 'Mais/menos-valias', value: aggregate.unrealizedGainPct, benchmark: 10, format: 'percentage' },
      { name: 'Rendimento total (não realizado + dividendos)', value: aggregate.totalReturnPct, benchmark: 12, format: 'percentage' },
      { name: 'Rendimento anualizado', value: annualized, benchmark: 8, format: 'percentage' },
      { name: 'Rendimento de dividendos (s/ custo)', value: aggregate.yieldOnCost, benchmark: 2, format: 'percentage' },
      { name: 'Volatilidade (anual)', value: vol, format: 'percentage' },
      { name: 'Perda máxima', value: maxDD, format: 'percentage' }
    ];
  }, [aggregate, performanceSummary]);

  const typeAllocation = useMemo(() => allocationByType(portfolioHoldings), [portfolioHoldings]);

  const handleExportCsv = useCallback(() => {
    const csv = holdingsToCsv(portfolioHoldings, aggregate);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carteira_${new Date().toISOString().substring(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [portfolioHoldings, aggregate]);

  const formatCurrency = (amount: number): string => formatEuro(amount);
  const formatPercentage = (percentage: number): string => formatPercent(percentage);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Análise da carteira"
        subtitle="Análise completa da alocação e do desempenho da sua carteira de investimentos"
        actions={
          <>
            <Card sx={{ p: 1.5, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">Concentração de risco (HHI)</Typography>
              <Typography variant="subtitle1" fontWeight="bold">{formatNumber(aggregate.riskConcentrationIndex, 3)}</Typography>
            </Card>
            <Card sx={{ p: 1.5, minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary">Maior contributo</Typography>
              <Typography variant="subtitle1" fontWeight="bold">{aggregate.topContributors[0]?.symbol || '-'}</Typography>
            </Card>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>Exportar CSV</Button>
          </>
        }
      />

      {/* Performance Summary Cards */}
      <AutoGrid min={240} gap={3} sx={{ mb: 3 }}>
        {performanceMetrics.slice(0, 3).map((metric, index) => (
          <Card key={metric.name}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {index === 0 && <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />}
                {index === 1 && <AnalyticsIcon color="primary" sx={{ mr: 1 }} />}
                {index === 2 && <TrendingUpIcon color="primary" sx={{ mr: 1 }} />}
                <Typography variant="subtitle2" color="text.secondary">
                  {metric.name}
                </Typography>
              </Box>
              {loading ? (
                <Skeleton variant="text" width="60%" height={32} />
              ) : (
                <Box>
                  <Typography variant="h5" component="div">
                    {metric.format === 'currency' 
                      ? formatCurrency(metric.value)
                      : formatPercentage(metric.value)
                    }
                  </Typography>
                  {metric.benchmark && (
                    <Typography 
                      variant="caption" 
                      color={metric.value >= metric.benchmark ? 'success.main' : 'warning.main'}
                    >
                      Referência: {formatPercentage(metric.benchmark)}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </AutoGrid>

      {/* Tabs for different analysis views */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="separadores da análise da carteira" variant="scrollable" scrollButtons="auto">
          <Tab label="Posições" />
          <Tab label="Alocação por setor" />
          <Tab label="Tipo de ativo" />
          <Tab label="Desempenho" />
        </Tabs>

        <TabPanel value={tabValue} index={0} idPrefix="portfolio" sx={{ p: 3 }}>
          {/* Holdings Overview */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Símbolo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Preço médio</TableCell>
                  <TableCell align="right">Preço atual</TableCell>
                  <TableCell align="right">Valor de mercado</TableCell>
                  <TableCell align="right">Lucro/Prejuízo</TableCell>
                  <TableCell align="right">Peso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 8 }).map((_, cellIndex) => (
                        <TableCell key={cellIndex}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : portfolioHoldings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma posição encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  portfolioHoldings.map((holding) => (
                    <TableRow key={holding.symbol} hover>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {holding.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={holding.investmentType} size="small" color={holding.investmentType === 'ETF' ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        {holding.investmentType === 'Cripto' 
                          ? formatCryptoQuantity(holding.shares)
                          : formatQuantity(holding.shares)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(holding.avgPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(holding.currentPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(holding.currentValue)}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {holding.unrealizedGain >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                          )}
                          <Box>
                            <Typography 
                              variant="body2" 
                              color={holding.unrealizedGain >= 0 ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {formatCurrency(holding.unrealizedGain)}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color={holding.unrealizedGain >= 0 ? 'success.main' : 'error.main'}
                            >
                              {formatPercentage(holding.unrealizedGainPct)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2">
                            {formatNumber(holding.weight, 1)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={holding.weight} 
                            sx={{ width: 50, height: 4 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1} idPrefix="portfolio" sx={{ p: 3 }}>
          {/* Sector Allocation */}
          <AutoGrid min={400} gap={3}>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sectorAllocation}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ sector, percentage }) => `${sector}: ${formatNumber(percentage, 1)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sectorAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Setor</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">Peso</TableCell>
                      <TableCell>Posições</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectorAllocation.map((sector, index) => (
                      <TableRow key={sector.sector}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                backgroundColor: COLORS[index % COLORS.length],
                                mr: 1,
                                borderRadius: 1
                              }} 
                            />
                            {sector.sector}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sector.value)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(sector.percentage, 1)}%
                        </TableCell>
                        <TableCell>
                          {sector.stocks.slice(0, 3).join(', ')}
                          {sector.stocks.length > 3 && ` +${sector.stocks.length - 3}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </AutoGrid>
        </TabPanel>

        <TabPanel value={tabValue} index={2} idPrefix="portfolio" sx={{ p: 3 }}>
          {/* Asset Type Allocation */}
          <AutoGrid min={300} gap={3}>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={typeAllocation}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${formatNumber(percentage, 1)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {typeAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Distribuição por tipo de investimento
              </Typography>
              {typeAllocation.map((type, index) => (
                <Box key={type.name} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{type.name}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(type.value)} ({formatNumber(type.percentage, 1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={type.percentage} 
                    sx={{ 
                      height: 8,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: COLORS[index % COLORS.length]
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </AutoGrid>
        </TabPanel>

        <TabPanel value={tabValue} index={3} idPrefix="portfolio" sx={{ p: 3 }}>
          {/* Performance (Time-series + Metrics) */}
          <Box sx={{ display: 'flex', flexDirection:'column', gap: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Desempenho da carteira</Typography>
              <Box>
                <select
                  value={timeframe}
                  onChange={e => setTimeframe(e.target.value as any)}
                  style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                >
                  <option value="6M">6 meses</option>
                  <option value="1Y">1 ano</option>
                  <option value="2Y">2 anos</option>
                  <option value="5Y">5 anos</option>
                  <option value="ALL">Tudo</option>
                </select>
              </Box>
            </Box>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Valor, custo investido e dividendos</Typography>
              {loading || !performanceSummary ? (
                <Skeleton variant="rectangular" height={320} />
              ) : (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceSummary.points}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => formatShortDate(String(v))} />
                      <YAxis />
                      <Tooltip formatter={(v,name)=> [formatCurrency(Number(v)), name]} labelFormatter={(label) => formatDate(String(label))} />
                      <Legend />
                      <Area type="monotone" dataKey="value" name="Valor da carteira" stroke="#1976d2" fill="#1976d2" fillOpacity={0.5} />
                      <Line type="monotone" dataKey="invested" name="Custo investido" stroke="#ff9800" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="cumulativeDividends" name="Dividendos acumulados" stroke="#4caf50" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
            <Paper sx={{ p:2 }}>
              <Typography variant="subtitle1" gutterBottom>Rendimentos mensais (aproximação ponderada pelo tempo)</Typography>
              {loading || !performanceSummary ? (
                <Skeleton variant="rectangular" height={280} />
              ) : (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceSummary.points}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(v) => formatShortDate(String(v))} />
                      <YAxis />
                      <Tooltip formatter={(v)=> `${formatNumber(Number(v))}%`} labelFormatter={(label) => formatDate(String(label))} />
                      <Bar dataKey="monthlyReturnPct" name="Rendimento mensal %" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
            <AutoGrid min={220} gap={3}>
              {performanceMetrics.map(metric => (
                <Card key={metric.name}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{metric.name}</Typography>
                    <Typography variant="h6" component="div">
                      {metric.format === 'currency' ? formatCurrency(metric.value) : formatPercentage(metric.value)}
                    </Typography>
                    {metric.benchmark && (
                      <Typography variant="caption" color="text.secondary">Referência: {formatPercentage(metric.benchmark)}</Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </AutoGrid>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default PortfolioAnalysisPage;
